const PII_KEYS = new Set([
  'email',
  'email_verified',
  'name',
  'given_name',
  'family_name',
  'preferred_username',
  'phone_number',
  'address',
  'birthdate',
  'picture',
  'image',
  'firstname',
  'lastname',
  'phone',
  'dateofbirth',
]);

/** Auth/credential fields that must never appear in logs. */
const SECRET_KEYS = new Set(['token', 'password', 'secret', 'authorization']);

const REDACTED = '[REDACTED]';

function shouldRedactKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return PII_KEYS.has(normalized) || SECRET_KEYS.has(normalized);
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false;
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

/**
 * ### redactPii
 *
 * @description
 * Deeply redacts known PII and secret fields from an object before logging.
 * Returns a new object — the original is never mutated.
 * Only recurses into plain objects and arrays; class instances (Error, Date, etc.)
 * are returned as-is. Cyclic references are safe — visited objects are skipped.
 *
 * @example
 * const safe = redactPii({ code: 'PROFILE_DATA', email: 'user@example.com', token: 'abc' });
 * // { code: 'PROFILE_DATA', email: '[REDACTED]', token: '[REDACTED]' }
 *
 * @param value - Any value that may contain PII or secrets
 * @returns A deep copy with sensitive field values replaced by '[REDACTED]'
 */
export function redactPii<T>(value: T): T {
  const seen = new WeakSet<object>();
  return redactPiiInternal(value, seen) as T;
}

function redactPiiInternal(value: unknown, seen: WeakSet<object>): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) return value;
    seen.add(value);
    return value.map((item) => redactPiiInternal(item, seen));
  }

  if (isPlainObject(value)) {
    if (seen.has(value)) return value;
    seen.add(value);
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = shouldRedactKey(key) ? REDACTED : redactPiiInternal(val, seen);
    }
    return result;
  }

  return value;
}
